import React, { useContext, useState, useEffect } from 'react';
import useSWR from 'swr';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { UserService } from '../services/user/user.service';
import { RootState } from '../../store/create-store';
import Loader from '../components/loader/loader.component';
import { isDevAuthBypass } from '../dev/dev-auth-bypass';

interface NotificationrDataContextInterface {
  count: number;
  setCount: React.Dispatch<React.SetStateAction<number>>;
}

interface INotificationProvider {
  children: React.ReactNode;
}

export const NotificationDataContext =
  React.createContext<NotificationrDataContextInterface>({
    count: 0,
    setCount: () => {},
  });

const NotificationDataProvider: React.FC<INotificationProvider> = ({
  children,
}) => {
  const userService = new UserService();

  const [count, setCount] = useState(0);
  const commonState = useSelector((state: RootState) => state.common);
  const { auth, user } = commonState;
  const location = useLocation();
  const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''));
  const inDashboard = location.pathname.startsWith('/u');
  const onAuthCallback =
    Boolean(hashParams.get('code')) || location.pathname === '/redirect';

  const canFetchCount =
    Boolean(auth?.token) &&
    Boolean(user) &&
    inDashboard &&
    !onAuthCallback &&
    !isDevAuthBypass;

  const { data: countData, isValidating } = useSWR(
    canFetchCount ? { key: 'getLoggedInUserNotification', auth } : null,
    () => userService.fetchNotificationsCount(),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  useEffect(() => {
    setCount(countData?.count || 0);
  }, [countData?.count]);

  const contextValue = { count, setCount };

  return (
    <NotificationDataContext.Provider value={contextValue}>
      {auth && isValidating && !user && !isDevAuthBypass ? (
        <Loader showLoader />
      ) : (
        children
      )}
    </NotificationDataContext.Provider>
  );
};

const useNotificationData = () => {
  const notificationData = useContext(NotificationDataContext);
  return notificationData;
};

export { useNotificationData };

export default NotificationDataProvider;
